import React, { useState, useEffect, useCallback, useMemo } from "react";
import { adminApiRequest } from "../utils/adminApi";
import AnalyticsSection from "./AnalyticsSection";
import AnalyticsModals from "./AnalyticsModals";
import PageLoader from "./PageLoader";

const getDefaultDates = () => {
  const today = new Date();
  const defaultEnd = today.toISOString().slice(0, 10);
  const defaultStart = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);

  return { defaultStart, defaultEnd };
};

const initialDetailsModal = {
  open: false,
  title: "",
  type: "",
  loading: false,
  rows: [],
  message: "",
};

const Analytics = () => {
  const { defaultStart, defaultEnd } = getDefaultDates();

  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [periodStart, setPeriodStart] = useState(defaultStart);
  const [periodEnd, setPeriodEnd] = useState(defaultEnd);

  const [modalData, setModalData] = useState(null);
  const [modalTitle, setModalTitle] = useState("");
  const [detailsModal, setDetailsModal] = useState(initialDetailsModal);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await adminApiRequest({
        script: "analytics",
        data: {
          start: periodStart,
          end: periodEnd,
        },
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load analytics");
      }

      setAnalytics(data.analytics);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [periodStart, periodEnd]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);




  const closeMainModal = () => {
    setModalData(null);
    setModalTitle("");
  };

  const closeDetailsModal = () => {
    setDetailsModal(initialDetailsModal);
  };

  const derived = useMemo(() => {
    if (!analytics) return null;

    const {
      ordersSummary = [],
      topItems = [],
      topCustomers = [],
      totalOrders = 0,
      totalRevenue = 0,
    } = analytics;

    const sortedOrdersSummary = [...ordersSummary].sort(
      (a, b) => new Date(b.period) - new Date(a.period),
    );

    const bestSeller = topItems[0] || null;

    // Filter out internal/test customers if relevant
    const actualCustomers = topCustomers.filter((cust) => !cust.is_internal);

    const topCustomerValue = actualCustomers[0] || null;

    const topCustomersByVolume = [...actualCustomers].sort(
      (a, b) => b.total_orders - a.total_orders,
    );

    const statCards = [
      {
        title: "Total Orders",
        value: totalOrders,
        icon: "🧾",
        accent: "bg-sky-500",
      },
      {
        title: "Gross Income",
        value: `£${Number(totalRevenue).toFixed(2)}`,
        icon: "💰",
        accent: "bg-emerald-500",
      },
      {
        title: "Best Seller",
        value:
          totalOrders > 0 && bestSeller?.quantity_sold > 0
            ? bestSeller.item_name
            : "—",
        icon: "🍽️",
        accent: "bg-violet-500",
      },
      {
        title: "Top Customer",
        value:
          totalOrders > 0 && topCustomerValue?.total_value > 0
            ? topCustomerValue.customer_name || topCustomerValue.customer_phone
            : "—",
        icon: "👑",
        accent: "bg-indigo-500",
      },
    ];

    const sectionConfigs = [
      {
        title: "Orders Summary",
        modalTitle: "Orders Summary",
        headers: ["Date", "Total Orders", "Total Revenue (£)"],
        data: sortedOrdersSummary,
        previewRows: sortedOrdersSummary.slice(0, 5).map((row) => ({
          cells: [
            row.period,
            row.total_orders,
            Number(row.total_revenue || 0).toFixed(2),
          ],
        })),
      },
      {
        title: "Top Selling Items",
        modalTitle: "All Top Selling Items",
        headers: ["Item Name", "Quantity Sold", "Total Revenue (£)"],
        data: topItems,
        previewRows: topItems.slice(0, 5).map((item) => ({
          cells: [
            item.item_name,
            item.quantity_sold,
            Number(item.total_revenue || 0).toFixed(2),
          ],
        })),
      },
      {
        title: "Top Customer (Value)",
        modalTitle: "Top Customers (Value)",
        headers: ["Name", "Phone", "Total Value (£)"],
        data: topCustomers,
        previewRows: topCustomers.slice(0, 5).map((cust) => ({
          isInternal: cust.is_internal,
          cells: [
            {
              type: "customer",
              label: cust.customer_name || "—",
              isInternal: cust.is_internal,
            },
            cust.customer_phone || "—",
            Number(cust.total_value || 0).toFixed(2),
          ],
        })),
      },
      {
        title: "Top Customer (Volume)",
        modalTitle: "Top Customers (Volume)",
        headers: ["Name", "Phone", "Total Orders"],
        data: topCustomersByVolume,
        previewRows: topCustomersByVolume.slice(0, 5).map((cust) => ({
          isInternal: cust.is_internal,
          cells: [
            {
              type: "customer",
              label: cust.customer_name || "—",
              isInternal: cust.is_internal,
            },
            cust.customer_phone || "—",
            cust.total_orders,
          ],
        })),
      },
    ];

    return {
      statCards,
      sectionConfigs,
    };
  }, [analytics]);

  if (loading) {
    return <PageLoader message="Analyzing Reports..." />;
  }

  if (error) {
    return (
      <p className="p-6 text-sm font-medium text-red-500">Error: {error}</p>
    );
  }

  if (!analytics || !derived) {
    return <p className="p-6 text-sm text-slate-500">No data available</p>;
  }

  return (
    <div className="h-full overflow-y-auto sidebar-scrollbar">
      <div className="space-y-6 p-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Analytics Overview
          </h1>

          <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Start Date
              </label>
              <input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-indigo-500 focus:bg-white"
              />
            </div>

            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">
                End Date
              </label>
              <input
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-indigo-500 focus:bg-white"
              />
            </div>

            <button
              className="rounded-xl bg-slate-900 px-6 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800 shadow-lg shadow-slate-200"
              onClick={fetchAnalytics}
            >
              Refresh Data
            </button>
          </div>
        </div>

        <AnalyticsSection type="stats" statCards={derived.statCards} />

        <AnalyticsSection
          type="tables"
          sectionConfigs={derived.sectionConfigs}
          onViewAll={(data, title) => {
            setModalData(data);
            setModalTitle(title);
          }}
        />


        <AnalyticsModals
          modalData={modalData}
          modalTitle={modalTitle}
          detailsModal={detailsModal}
          onCloseMain={closeMainModal}
          onCloseDetails={closeDetailsModal}
        />
      </div>
    </div>
  );
};

export default Analytics;
